import { useCallback, useEffect, useState } from "react";
import { type CompanyWithUI } from "@/components/shared/CompanyCardHorizontal";
import { useAuth } from "@/hooks/useAuth";
import { useUser } from "@/context/UserContext";
import { mapQuickSearchCompanies } from "@/pages/discover/utils/discoverMappers";
import { readGuestShortlistCompanyIds } from "@/shared/utils/guestShortlistStorage";
import trpc from "@/shared/utils/trpc";

const PREVIEW_LIMIT = 4;

export function useSectorCompaniesPreview(slug: string) {
  const { isAuthenticated } = useAuth();
  const { shortlistedIds } = useUser();
  const [companies, setCompanies] = useState<CompanyWithUI[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!slug.trim()) {
      setCompanies([]);
      setLoading(false);
      setError("Sector not found.");
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await trpc.company.quickSearch.query({
        query: "",
        page: 1,
        limit: PREVIEW_LIMIT,
        sector: slug,
      });

      const mapped = mapQuickSearchCompanies(response.companies || [], {
        isAuthenticated,
        guestShortlistedIds: new Set(readGuestShortlistCompanyIds()),
        shortlistedIds,
      });

      setCompanies(mapped.slice(0, PREVIEW_LIMIT));
    } catch {
      setCompanies([]);
      setError("Unable to load companies.");
    } finally {
      setLoading(false);
    }
  }, [slug, isAuthenticated, shortlistedIds]);

  useEffect(() => {
    void load();
  }, [load]);

  return { companies, setCompanies, loading, error, reload: load };
}
